import styled from '@emotion/styled'
import React, { useEffect } from 'react'
import { Sex } from '@/firebase/firestore/Sex'
import { VoiceChat } from '@/firebase/firestore/VoiceChats'
import Image from 'next/image'
import fitty from 'fitty'
import { Platform } from '@/firebase/firestore/Platform'

type Props = {
  targetId: string
  templateColor: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7
  uid: string
  name: string
  sex: Sex
  platform: Array<Platform>
  voiceChat: Array<VoiceChat>
  hunterRank: number
  masterRank: number
  hunterHistory: string
  fontColor: string
  fontFamily: string
  playTimeWeekDayBegin: string
  playTimeWeekDayEnd: string
  playTimeWeekEndBegin: string
  playTimeWeekEndEnd: string
  hitokoto: string
  weapons: number[]
}

export const MonsterHunterRiseSunBreak1: React.FC<Props> = ({
  targetId,
  uid,
  name,
  fontColor,
  fontFamily,
  templateColor,
  platform,
  voiceChat,
  hunterRank,
  masterRank,
  hunterHistory,
  hitokoto,
  weapons,
}) => {
  useEffect(() => {
    fitty('#name', {
      maxSize: 24,
      minSize: 12,
    })
    fitty('#uid', {
      maxSize: 13,
      minSize: 8,
    })
    fitty('#rank', {
      maxSize: 18,
      minSize: 12,
    })
    fitty('#voiceChat', {
      maxSize: 18,
      minSize: 15,
    })
    fitty('#hunterHistory', {
      maxSize: 24,
      minSize: 12,
    })
    fitty('#hitokoto', {
      maxSize: 18,
      minSize: 15,
    })
  }, [])
  return (
    <Container id={targetId} fontFamily={fontFamily} fontColor={fontColor}>
      <Image
        width="960"
        height="760"
        priority={true}
        unoptimized={true}
        src={`/static/images/profiles/monsterHunterRiseSunBreak1/${templateColor}.jpeg`}
        alt="モンハンライズサンブレイク自己紹介カード"
      />
      <Name>
        <span id="name">{name}</span>
      </Name>
      <Id>
        <span id="uid">{uid}</span>
      </Id>
      {platform.includes(3) && <Platform0 />}
      {platform.includes(0) && <Platform1 />}
      <RankText id="rank" color={fontColor}>
        {hunterRank ? `HR${hunterRank}` : ''}
        {hunterRank && masterRank ? '・' : ''}{' '}
        {masterRank ? `MR${masterRank}` : ''}
      </RankText>
      <VCText id="voiceChat" color={fontColor}>
        {voiceChat.includes(0) && ' Skype '}
        {voiceChat.includes(1) && ' LINE '}
        {voiceChat.includes(2) && ' プレステ '}
        {voiceChat.includes(3) && ' Discord '}
        {voiceChat.includes(5) && ' Switch '}
      </VCText>
      <HunterHistory>
        <span id={'hunterHistory'}>{hunterHistory}</span>
      </HunterHistory>
      {weapons.includes(0) && <Weapon0 />}
      {weapons.includes(1) && <Weapon1 />}
      {weapons.includes(2) && <Weapon2 />}
      {weapons.includes(3) && <Weapon3 />}
      {weapons.includes(4) && <Weapon4 />}
      {weapons.includes(5) && <Weapon5 />}
      {weapons.includes(6) && <Weapon6 />}
      {weapons.includes(7) && <Weapon7 />}
      {weapons.includes(8) && <Weapon8 />}
      {weapons.includes(9) && <Weapon9 />}
      {weapons.includes(10) && <Weapon10 />}
      {weapons.includes(11) && <Weapon11 />}
      {weapons.includes(12) && <Weapon12 />}
      {weapons.includes(13) && <Weapon13 />}
      <Hitokoto>
        <span id={'hitokoto'}>{hitokoto}</span>
      </Hitokoto>
    </Container>
  )
}

const Container = styled.div<{ fontFamily: string; fontColor: string }>`
  position: relative;
  width: 100%;
  line-height: 0;
  * {
    line-height: 1;
    font-family: ${({ fontFamily }) => fontFamily};
    color: ${({ fontColor }) => fontColor};
    border-color: ${({ fontColor }) => fontColor};
  }
`

const Name = styled.div`
  position: absolute;
  top: 17%;
  left: 4%;
  width: 42%;
  height: 7%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const Id = styled.div`
  position: absolute;
  top: 31.5%;
  left: 4%;
  text-align: left;
  width: 30%;
  display: flex;
  align-items: center;

  span {
    font-weight: bold;
    line-height: 13px;
  }
`

const PlatformBase = styled.div`
  position: absolute;
  top: 32%;
  left: 16%;
  border-style: solid;
  border-width: 3px;
  border-radius: 12.5px;
  width: 60px;
  height: 25px;
`
const Platform0 = styled(PlatformBase)``
const Platform1 = styled(PlatformBase)`
  left: 31%;
`

const RankText = styled.div`
  position: absolute;
  top: 52%;
  left: 0%;
  width: 27%;
  height: 20%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 1.2;
  }
`

const VCText = styled.div`
  position: absolute;
  top: 52%;
  left: 22.5%;
  width: 30%;
  height: 7%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;

  span {
    text-align: center;
    font-weight: bold;
    line-height: 0.9;
  }
`

const HunterHistory = styled.div`
  position: absolute;
  top: 17%;
  left: 66%;
  text-align: center;
  width: 16%;
  height: 20%;
  font-weight: bold;
  word-break: break-all;
  line-height: 13px;
`

const WeaponBase = styled.div`
  position: absolute;
  top: 70%;
  left: 3%;
  border-style: solid;
  border-width: 2.5px;
  border-radius: 50%;
  width: 30px;
  height: 30px;
`

const Weapon0 = styled(WeaponBase)``
const Weapon1 = styled(WeaponBase)`
  left: 10%;
`
const Weapon2 = styled(WeaponBase)`
  left: 17%;
`
const Weapon3 = styled(WeaponBase)`
  left: 24%;
`
const Weapon4 = styled(WeaponBase)`
  left: 30.5%;
`

const Weapon5 = styled(WeaponBase)`
  left: 37%;
`
const Weapon6 = styled(WeaponBase)`
  //右端
  left: 44%;
`
const Weapon7 = styled(WeaponBase)`
  top: 81%;
  left: 3%;
`
const Weapon8 = styled(WeaponBase)`
  top: 81%;
  left: 10%;
`
const Weapon9 = styled(WeaponBase)`
  top: 81%;
  left: 17%;
`

const Weapon10 = styled(WeaponBase)`
  top: 81%;
  left: 24%;
`

const Weapon11 = styled(WeaponBase)`
  top: 81%;
  left: 30.5%;
`

const Weapon12 = styled(WeaponBase)`
  top: 81%;
  left: 37%;
`

const Weapon13 = styled(WeaponBase)`
  top: 81%;
  left: 44%;
`

const Hitokoto = styled.div`
  position: absolute;
  top: 28%;
  left: 53%;
  height: 40%;
  width: 42%;
  word-break: break-all;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    font-weight: bold;
    line-height: 1.2;
  }
`
